import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiKeyInput from './ApiKeyInput';
import { SettingsState } from '@/hooks/useSettings';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { extractSpreadsheetId, getSpreadsheetUrl, validateSpreadsheet } from '@/utils/googleSheets';

interface ApiKeysFormProps {
    // Use the unified settings types
    settings: SettingsState;
    updateSetting: (category: string, key: string, value: any) => void;
    showKeys: boolean;
}

const isDevelopment = process.env.NODE_ENV === 'development';

const ApiKeysForm: React.FC<ApiKeysFormProps> = ({
    settings,
    updateSetting,
    showKeys,
}) => {
    const { t } = useTranslation();
    const [sheetStatus, setSheetStatus] = useState<'idle' | 'checking' | 'valid' | 'invalid'>('idle');

    // Get feature flags for convenience
    const featureFlags = settings?.featureFlags || {};

    // Define the API key fields with category support
    const apiKeyFields = [
        {
            name: 'openrouter',
            category: '',
            label: t('settings.apiKeys.openrouter.label'),
            helpText: t('settings.apiKeys.openrouter.help'),
            placeholder: 'sk-or-...',
            sensitive: true,
            externalUrl: 'https://openrouter.ai/settings/keys'
        },
        // NOTE: Only use OpenRouter for now, comment out other API keys until they are needed
        // {
        //     name: 'openai',
        //     category: '',
        //     label: t('settings.apiKeys.openai.label'),
        //     helpText: t('settings.apiKeys.openai.help'),
        //     placeholder: 'sk-...'
        // },
        // ...(isDevelopment ? [{
        //     name: 'gemini',
        //     category: '',
        //     label: t('settings.apiKeys.gemini.label'),
        //     helpText: t('settings.apiKeys.gemini.help'),
        //     placeholder: 'AIzaSy...'
        // }] : []),
        // {
        //     name: 'mistral',
        //     category: '',
        //     label: t('settings.apiKeys.mistral.label'),
        //     helpText: t('settings.apiKeys.mistral.help'),
        //     placeholder: ''
        // },
        // {
        //     name: 'openchat',
        //     category: '',
        //     label: t('settings.apiKeys.openchat.label'),
        //     helpText: t('settings.apiKeys.openchat.help'),
        //     placeholder: ''
        // },
        // {
        //     name: 'perflexity',
        //     category: '',
        //     label: t('settings.apiKeys.perflexity.label'),
        //     helpText: t('settings.apiKeys.perflexity.help'),
        //     placeholder: 'pplx-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'
        // },
        {
            name: 'googleSheetApiKey',
            category: '',
            label: t('settings.apiKeys.googleSheetApiKey.label'),
            helpText: t('settings.apiKeys.googleSheetApiKey.help'),
            placeholder: 'AIzaSy...',
            sensitive: true,
            externalUrl: 'https://console.cloud.google.com/apis/credentials'
        },
        {
            name: 'spreadsheetId',
            category: '',
            label: t('settings.apiKeys.spreadsheetId.label'),
            helpText: t('settings.apiKeys.spreadsheetId.help'),
            placeholder: t('settings.apiKeys.spreadsheetId.placeholder')
        },
        {
            name: 'sheetNameKnowledgeBase',
            category: '',
            label: t('settings.apiKeys.sheetNameKnowledgeBase.label'),
            helpText: t('settings.apiKeys.sheetNameKnowledgeBase.help'),
            placeholder: 'KnowledgeBase'
        },
        {
            name: 'sheetNameInterviewQuestions',
            category: '',
            label: t('settings.apiKeys.sheetNameInterviewQuestions.label'),
            helpText: t('settings.apiKeys.sheetNameInterviewQuestions.help'),
            placeholder: 'InterviewQuestions'
        }
    ];

    // Feature toggles
    const featureToggles = [
        {
            key: "autoSaveKnowledge",
            label: t('settings.features.autoSaveKnowledge.label'),
            description: t('settings.features.autoSaveKnowledge.description')
        },
        {
            key: "autoSaveInterview",
            label: t('settings.features.autoSaveInterview.label'),
            description: t('settings.features.autoSaveInterview.description')
        }
    ];

    // Helper function to get value from settings object
    const getValue = (field: { name: string, category: string }) => {
        if (!field.category) {
            return settings[field.name] || '';
        }
        return settings && settings[field.category]?.[field.name] || '';
    };

    // Handle setting value change
    const handleValueChange = (field: { name: string, category: string }, value: string) => {
        const nextValue = field.name === 'spreadsheetId' ? extractSpreadsheetId(value) : value;
        if (field.name === 'spreadsheetId') setSheetStatus('idle');
        updateSetting(field.category, field.name, nextValue);
    };

    const checkSpreadsheet = async () => {
        const apiKey = String(settings.googleSheetApiKey || '');
        const spreadsheetId = String(settings.spreadsheetId || '');
        if (!apiKey || !spreadsheetId) return;

        setSheetStatus('checking');
        setSheetStatus(await validateSpreadsheet(apiKey, spreadsheetId) ? 'valid' : 'invalid');
    };

    const spreadsheetUrl = getSpreadsheetUrl(String(settings.spreadsheetId || ''));
    const sheetStatusNode = sheetStatus === 'idle' ? null : (
        <p className={`flex items-center gap-1.5 text-sm ${sheetStatus === 'valid' ? 'text-green-600' : sheetStatus === 'invalid' ? 'text-red-600' : 'text-gray-500'}`}>
            {sheetStatus === 'checking' && <Loader2 className="h-4 w-4 animate-spin" />}
            {sheetStatus === 'valid' && <CheckCircle2 className="h-4 w-4" />}
            {sheetStatus === 'invalid' && <XCircle className="h-4 w-4" />}
            {t(`settings.apiKeys.spreadsheetId.status.${sheetStatus}`)}
        </p>
    );

    useEffect(() => {
        const apiKey = String(settings.googleSheetApiKey || '');
        const spreadsheetId = String(settings.spreadsheetId || '');
        if (!apiKey || !spreadsheetId) {
            setSheetStatus('idle');
            return;
        }

        setSheetStatus('checking');
        const timeoutId = window.setTimeout(async () => {
            setSheetStatus(await validateSpreadsheet(apiKey, spreadsheetId) ? 'valid' : 'invalid');
        }, 700);

        return () => window.clearTimeout(timeoutId);
    }, [settings.googleSheetApiKey, settings.spreadsheetId]);

    // Handle feature flag change
    const handleFeatureFlagChange = (key: string, value: boolean) => {
        updateSetting('featureFlags', key, value);
    };

    return (
        <div className="space-y-8">
            <div className="grid gap-6">
                {apiKeyFields.map(field => (
                    <ApiKeyInput
                        key={`${field.category}-${field.name}`}
                        name={field.name}
                        label={field.label}
                        value={getValue(field)}
                        onChange={(value) => handleValueChange(field, value)}
                        placeholder={field.placeholder}
                        helpText={field.helpText}
                        showKey={showKeys}
                        sensitive={'sensitive' in field && Boolean(field.sensitive)}
                        externalUrl={field.name === 'spreadsheetId' || field.name.startsWith('sheetName')
                            ? spreadsheetUrl
                            : ('externalUrl' in field ? field.externalUrl : undefined)}
                        externalLabel={t('settings.apiKeys.openLinkedPage')}
                        onBlur={field.name === 'spreadsheetId' ? checkSpreadsheet : undefined}
                        status={field.name === 'spreadsheetId' ? sheetStatusNode : undefined}
                    />
                ))}
            </div>

            <div className="border rounded-md p-4 space-y-4">
                <h3 className="text-lg font-medium">{t('settings.features.title')}</h3>

                <div className="space-y-4">
                    {featureToggles.map(feature => (
                        <div key={feature.key} className="flex items-center justify-between">
                            <div>
                                <Label htmlFor={feature.key} className="font-medium">
                                    {feature.label}
                                </Label>
                                <p className="text-sm text-muted-foreground">{feature.description}</p>
                            </div>
                            <Switch
                                id={feature.key}
                                checked={!!featureFlags[feature.key]}
                                variant="success"
                                onCheckedChange={(checked) => handleFeatureFlagChange(feature.key, checked)}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ApiKeysForm;
